/**
 * avatarPhoto.js — is a stored profile photo a real photo? (RW-3825)
 *
 * Every avatar falls back to initials when there is no photoURL, and most also
 * fall back when the image fails to load. Neither catches a stock "no photo"
 * graphic: it is a URL, and it loads. Mason Pearce's card showed one (a
 * crossed-out person on grey) where everyone else without a photo gets the
 * standard avatar.
 *
 * `usablePhotoURL(url)` returns the URL, or null when it is empty or a known
 * placeholder, so the caller's existing no-photo branch renders. Use it
 * wherever a person's photo is rendered: in the condition AND the src.
 *
 * Patterns, from where our photo URLs come from:
 *   - Google sign-in (Auth photoURL → users/{uid}.photoURL, setUserRole): the
 *     legacy default silhouette (…/AAAAAAAAAAI/AAAAAAAAAAA/…/photo.jpg), the
 *     `/a/default-user` avatar, and gstatic's accounts/ui/avatar_*.png;
 *   - Gravatar: the all-zero hash, or a URL that forces the default (f=y);
 *   - pasted or imported URLs (Workvivo import, ProfileEditor, Darwin/vendor
 *     exports): a file named like a stock placeholder — no-image.png,
 *     default-avatar.jpg, missing.png (Rails/Paperclip's default) and so on.
 * Only the FILE NAME is matched for the last group, so a real photo stored
 * under a folder called "placeholder" is kept.
 */

const PLACEHOLDER_URL_RES = [
  // Google: legacy default silhouette — the two all-A path segments.
  /\/AAAAAAAAAAI\/AAAAAAAAAAA\//,
  // Google: the "default-user" avatar.
  /googleusercontent\.com\/a\/default-user(?:[=/?#]|$)/i,
  // Google accounts' stock "no avatar" images.
  /gstatic\.com\/accounts\/ui\/avatar_/i,
  // Gravatar: the empty (all-zero) hash.
  /gravatar\.com\/avatar\/0{32}(?:[./?#]|$)/i,
  // Gravatar: forced default (f=y / forcedefault=y) — never the person's photo.
  /gravatar\.com\/avatar\/[^#]*[?&](?:f|forcedefault)=(?:y|yes|1|true)(?:[&#]|$)/i,
];

// Stock placeholder file names (the last path segment, decoded).
const PLACEHOLDER_FILE_RE = new RegExp(
  '^(?:'
    + 'no[-_ ]?(?:image|img|photo|picture|pic|avatar|profile|user)'
    + '|default[-_ ]?(?:avatar|user|profile|photo|picture|pic|headshot|image)'
    + '|(?:avatar|profile|user|photo|headshot)[-_ ]?(?:placeholder|default|blank|missing|empty)'
    + '|placeholder(?:[-_ ]?(?:avatar|user|profile|photo|image|person))?'
    + '|blank[-_ ]?(?:avatar|profile|user|photo|person)'
    + '|image[-_ ]?not[-_ ]?(?:found|available)'
    + '|anonymous(?:[-_ ]?(?:user|avatar))?'
  + ')(?:[-_ @][-\\w@ ]*)?\\.(?:png|jpe?g|gif|svg|webp)$',
  'i',
);
// Rails/Paperclip's default — exact, so "missing-dog.jpg" is not caught.
const MISSING_FILE_RE = /^missing(?:@\dx)?\.(?:png|jpe?g|gif)$/i;

function lastPathSegment(url) {
  let path = url;
  try {
    path = new URL(url).pathname;
  } catch (_) {
    path = url.split(/[?#]/)[0];
  }
  let decoded = path;
  // Firebase Storage keeps the object path %2F-encoded inside /o/<path>.
  try { decoded = decodeURIComponent(path); } catch (_) { /* keep raw */ }
  const parts = decoded.split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1] : '';
}

/** True when `url` is a known stock "no photo" image (or not a usable URL at all). */
export function isPlaceholderPhotoURL(url) {
  if (typeof url !== 'string') return true;
  const u = url.trim();
  if (!u || u === 'null' || u === 'undefined') return true;
  if (PLACEHOLDER_URL_RES.some((re) => re.test(u))) return true;
  const file = lastPathSegment(u);
  return PLACEHOLDER_FILE_RE.test(file) || MISSING_FILE_RE.test(file);
}

/** The photo URL to render, or null so the caller shows its standard avatar. */
export function usablePhotoURL(url) {
  return isPlaceholderPhotoURL(url) ? null : url.trim();
}
